import { supabase } from "../supabase";
import { formatSupabaseError } from "./estimates";
import { requireInvoiceReview } from "./financialGuards";
import {
  computeEstimateTaxSummary,
  normalizeEstimateTaxLinesForSave,
} from "./estimateTax";
import { formatCurrency } from "./estimateTotals";
import {
  createInvoiceLineId,
  getInvoiceLineTypeMeta,
  INVOICE_LINE_TYPE,
} from "./invoiceLineTypes";
import { normalizeLogHours } from "./labourCost";
import { findMatchingQuoteLine } from "./projectQuoteLineMatching";
import { loadProjectQuoteLines } from "./projectQuoteSnapshots";
import { applyOrganizationScopeForActor } from "./orgQueryScope";

export { findMatchingQuoteLine } from "./projectQuoteLineMatching";
export {
  INVOICE_LINE_TYPE,
  INVOICE_LINE_TYPE_REGISTRY,
  getAddableInvoiceLineCategories,
  getInvoiceLineTypeMeta,
  createInvoiceLineId,
  isPreservedManualInvoiceLine,
} from "./invoiceLineTypes";

export const PRICING_SOURCE = Object.freeze({
  SEED_CATALOG_SELL: "seed_catalog_sell",
  SEED_QUOTE_SNAPSHOT: "seed_quote_snapshot",
  SEED_COST_CONTEXT: "seed_cost_context",
  MANUAL_OVERRIDE: "manual_override",
});

export const LINE_STATUS = Object.freeze({
  ACTIVE: "active",
  REMOVED: "removed",
});

function wrapSupabaseError(error) {
  const wrapped = new Error(formatSupabaseError(error));
  wrapped.cause = error;
  throw wrapped;
}

export function roundCurrency(amount) {
  return Math.round((Number(amount) + Number.EPSILON) * 100) / 100;
}

export function computeReviewLineTotal(quantity, unitAmount, discountPercent = 0) {
  const qty = Number(quantity) || 0;
  const unit = Number(unitAmount) || 0;
  const discount = Number(discountPercent) || 0;
  const gross = qty * unit;
  return roundCurrency(gross * (1 - discount / 100));
}

/** Reindex `sort_order` to match array position (display = persist order). */
export function reindexInvoiceLineSortOrder(lines = []) {
  return (lines || []).map((line, index) => ({
    ...line,
    sort_order: index,
  }));
}

/**
 * Move a draft line within the list and reindex sort_order.
 * @param {"up" | "down"} direction
 */
export function moveInvoiceLineItem(lines = [], fromIndex, direction) {
  const rows = Array.isArray(lines) ? [...lines] : [];
  if (
    fromIndex < 0 ||
    fromIndex >= rows.length ||
    (direction !== "up" && direction !== "down")
  ) {
    return reindexInvoiceLineSortOrder(rows);
  }

  const toIndex = direction === "up" ? fromIndex - 1 : fromIndex + 1;
  if (toIndex < 0 || toIndex >= rows.length) {
    return reindexInvoiceLineSortOrder(rows);
  }

  const [moved] = rows.splice(fromIndex, 1);
  rows.splice(toIndex, 0, moved);
  return reindexInvoiceLineSortOrder(rows);
}

/**
 * Build a manual invoice line (service, and future credit/discount/adjustment).
 * Not linked to operational labour or material source records.
 */
export function buildManualInvoiceLine({
  lineType = INVOICE_LINE_TYPE.SERVICE,
  description,
  detail = null,
  quantity = 1,
  unitLabel = "ea",
  unitAmount = 0,
  discountPercent = 0,
  taxable = true,
  sortOrder = 0,
  metadata = {},
  lineId = null,
} = {}) {
  const qty = Number(quantity) || 0;
  const unit = Number(unitAmount) || 0;
  const discount = Number(discountPercent) || 0;
  const lineTotal = computeReviewLineTotal(qty, unit, discount);

  return {
    line_id: lineId || createInvoiceLineId(),
    sort_order: sortOrder,
    line_type: lineType,
    description: (description || getInvoiceLineTypeMeta(lineType).label).trim(),
    detail: detail?.trim?.() ? detail.trim() : detail || null,
    quantity: qty,
    unit_label: unitLabel || "ea",
    unit_amount: unit,
    line_total: lineTotal,
    discount_percent: discount,
    line_status: LINE_STATUS.ACTIVE,
    pricing_source: PRICING_SOURCE.MANUAL_OVERRIDE,
    reference_unit_cost: null,
    reference_quoted_unit_price: null,
    reference_catalog_sell: null,
    source_quote_line_snapshot_id: null,
    source_time_log_id: null,
    source_usage_id: null,
    metadata: {
      taxable: taxable !== false,
      ...metadata,
    },
  };
}

export function buildServiceLineFromCatalog(
  service,
  {
    name,
    description,
    quantity,
    unitPrice,
    taxable,
    sortOrder = 0,
    lineId = null,
  } = {},
) {
  const resolvedName = (name || service?.name || "Service").trim();
  const resolvedDetail =
    description != null && String(description).trim()
      ? String(description).trim()
      : service?.description || null;
  const qty =
    quantity != null && quantity !== ""
      ? Number(quantity)
      : Number(service?.default_quantity) || 1;
  const unit =
    unitPrice != null && unitPrice !== ""
      ? Number(unitPrice)
      : Number(service?.default_unit_price) || 0;
  const isTaxable =
    taxable != null ? taxable !== false : service?.taxable !== false;

  return buildManualInvoiceLine({
    lineType: INVOICE_LINE_TYPE.SERVICE,
    description: resolvedName,
    detail: resolvedDetail,
    quantity: qty,
    unitLabel: service?.unit_label || "ea",
    unitAmount: unit,
    taxable: isTaxable,
    sortOrder,
    lineId,
    metadata: {
      taxable: isTaxable,
      service_id: service?.service_id || null,
      service_catalog: true,
    },
  });
}

export function buildSeedLineFromMaterialUsage(
  row,
  { quoteLine = null, catalogSell = null, sortOrder = 0 },
) {
  const quantity = Number(row.quantity || 0);
  const unitLabel = row.unit_of_measure_snapshot || "ea";
  const referenceUnitCost = Number(row.unit_cost_snapshot || 0);
  const referenceQuotedUnitPrice = quoteLine
    ? Number(quoteLine.quoted_unit_price_snapshot || 0)
    : null;
  const referenceCatalogSell =
    catalogSell != null && catalogSell !== "" ? Number(catalogSell) : null;

  let unitAmount = 0;
  let pricingSource = PRICING_SOURCE.SEED_CATALOG_SELL;

  if (quoteLine) {
    unitAmount = Number(quoteLine.quoted_unit_price_snapshot || 0);
    pricingSource = PRICING_SOURCE.SEED_QUOTE_SNAPSHOT;
  } else if (referenceCatalogSell != null && referenceCatalogSell > 0) {
    unitAmount = referenceCatalogSell;
    pricingSource = PRICING_SOURCE.SEED_CATALOG_SELL;
  }

  const lineTotal = computeReviewLineTotal(quantity, unitAmount, 0);

  return {
    sort_order: sortOrder,
    line_type: "material",
    description: row.material_name_snapshot || "Material",
    detail: `${quantity} × ${formatCurrency(referenceUnitCost)} cost /${unitLabel}`,
    quantity,
    unit_label: unitLabel,
    unit_amount: unitAmount,
    line_total: lineTotal,
    discount_percent: 0,
    line_status: LINE_STATUS.ACTIVE,
    pricing_source: pricingSource,
    reference_unit_cost: referenceUnitCost,
    reference_quoted_unit_price: referenceQuotedUnitPrice,
    reference_catalog_sell: referenceCatalogSell,
    source_quote_line_snapshot_id: quoteLine?.line_snapshot_id ?? null,
    source_usage_id: row.usage_id ?? null,
    metadata: {
      usage_date: row.usage_date || null,
      sku_snapshot: row.sku_snapshot || null,
      notes: row.notes || null,
    },
  };
}

export function buildSeedLineFromTimeLog(
  log,
  { quoteLine = null, sortOrder = 0 },
) {
  const hours = normalizeLogHours(log);
  const referenceUnitCost = Number(log.hourly_rate || 0);
  const referenceQuotedUnitPrice = quoteLine
    ? Number(quoteLine.quoted_unit_price_snapshot || 0)
    : null;

  let unitAmount = 0;
  let pricingSource = PRICING_SOURCE.SEED_COST_CONTEXT;

  if (quoteLine) {
    unitAmount = Number(quoteLine.quoted_unit_price_snapshot || 0);
    pricingSource = PRICING_SOURCE.SEED_QUOTE_SNAPSHOT;
  } else {
    unitAmount = referenceUnitCost;
    pricingSource = PRICING_SOURCE.SEED_COST_CONTEXT;
  }

  const lineTotal = computeReviewLineTotal(hours, unitAmount, 0);

  return {
    sort_order: sortOrder,
    line_type: "labour",
    description: log.employee || "Unknown Employee",
    detail: `${hours.toFixed(2)} hrs · cost ${formatCurrency(referenceUnitCost)}/hr`,
    quantity: hours,
    unit_label: "hrs",
    unit_amount: unitAmount,
    line_total: lineTotal,
    discount_percent: 0,
    line_status: LINE_STATUS.ACTIVE,
    pricing_source: pricingSource,
    reference_unit_cost: referenceUnitCost,
    reference_quoted_unit_price: referenceQuotedUnitPrice,
    reference_catalog_sell: null,
    source_quote_line_snapshot_id: quoteLine?.line_snapshot_id ?? null,
    source_time_log_id: log.id ?? null,
    metadata: {
      date_iso: log.date_iso || null,
      employee: log.employee || null,
      work_completed: log.work_completed || null,
    },
  };
}

export function buildSeedLinesFromOperationalData({
  materialRows = [],
  timeLogs = [],
  quoteLines = [],
  catalogByMaterialId = new Map(),
}) {
  const labourLines = timeLogs.map((log, index) => {
    const quoteLine = findMatchingQuoteLine(quoteLines, {
      lineType: "labour",
      quoteLineSnapshotId: log.quote_line_snapshot_id,
    });
    return buildSeedLineFromTimeLog(log, { quoteLine, sortOrder: index });
  });

  const materialLines = materialRows.map((row, index) => {
    const quoteLine =
      findMatchingQuoteLine(quoteLines, {
        lineType: "material",
        materialId: row.material_id,
        quoteLineSnapshotId: row.quote_line_snapshot_id,
      }) ||
      findMatchingQuoteLine(quoteLines, {
        lineType: "material",
        quoteLineSnapshotId: row.quote_line_snapshot_id,
      });

    const catalogSell = row.material_id
      ? catalogByMaterialId.get(row.material_id)?.sell_price
      : null;

    return buildSeedLineFromMaterialUsage(row, {
      quoteLine,
      catalogSell,
      sortOrder: labourLines.length + index,
    });
  });

  return [...labourLines, ...materialLines];
}

export function computeActiveLineSubtotals(lineItems = []) {
  const active = (lineItems || []).filter(
    (line) => (line.line_status || LINE_STATUS.ACTIVE) === LINE_STATUS.ACTIVE,
  );

  const sumBucket = (bucket) =>
    roundCurrency(
      active
        .filter((line) => {
          const meta = getInvoiceLineTypeMeta(line.line_type);
          if (!meta.contributesToSubtotal) return false;
          return meta.subtotalBucket === bucket;
        })
        .reduce((sum, line) => sum + Number(line.line_total || 0), 0),
    );

  const subtotalLabour = sumBucket("labour");
  const subtotalMaterials = sumBucket("materials");
  const subtotalServices = sumBucket("services");
  const subtotalOther = sumBucket("other");

  return {
    subtotalLabour,
    subtotalMaterials,
    subtotalServices,
    subtotalOther,
  };
}

/**
 * Category rows for the Invoice Summary "Totals" card.
 * `invoices` persists only subtotal_labour / subtotal_materials (plus the
 * post-adjustment `subtotal`), so Services / Other are derived from the
 * invoice's line items via the canonical bucket helper. Without line items
 * the persisted header columns are the only source available.
 */
export function resolveInvoiceSummaryBuckets(invoice) {
  const lines = invoice?.lineItems;
  if (Array.isArray(lines) && lines.length > 0) {
    const b = computeActiveLineSubtotals(lines);
    return {
      labour: b.subtotalLabour,
      materials: b.subtotalMaterials,
      services: b.subtotalServices,
      other: b.subtotalOther,
      lineSubtotal: roundCurrency(
        b.subtotalLabour + b.subtotalMaterials + b.subtotalServices + b.subtotalOther,
      ),
    };
  }
  const labour = Number(invoice?.subtotal_labour || 0);
  const materials = Number(invoice?.subtotal_materials || 0);
  return {
    labour,
    materials,
    services: 0,
    other: 0,
    lineSubtotal: roundCurrency(labour + materials),
  };
}

export function computeInvoiceReviewRollups({
  lineItems = [],
  projectDiscountPercent = 0,
  goodwillAdjustment = 0,
  taxLines = [],
}) {
  const {
    subtotalLabour,
    subtotalMaterials,
    subtotalServices,
    subtotalOther,
  } = computeActiveLineSubtotals(lineItems);
  const lineSubtotal = roundCurrency(
    subtotalLabour + subtotalMaterials + subtotalServices + subtotalOther,
  );
  const afterProjectDiscount = roundCurrency(
    lineSubtotal * (1 - (Number(projectDiscountPercent) || 0) / 100),
  );
  const taxableSubtotal = roundCurrency(
    afterProjectDiscount + (Number(goodwillAdjustment) || 0),
  );
  const taxSummary = computeEstimateTaxSummary(taxableSubtotal, taxLines);

  return {
    subtotalLabour,
    subtotalMaterials,
    subtotalServices,
    subtotalOther,
    subtotal: taxableSubtotal,
    totalTax: taxSummary.totalTax,
    totalAmount: taxSummary.grandTotal,
    taxSummary,
    lineSubtotal,
    afterProjectDiscount,
  };
}

export async function loadCatalogSellByMaterialId(materialIds = [], actor = null) {
  if (!materialIds.length) return new Map();

  let query = supabase
    .from("materials")
    .select("material_id, sell_price")
    .in("material_id", materialIds);

  if (actor) {
    query = await applyOrganizationScopeForActor(query, actor, supabase, {
      tableName: "materials",
    });
  }

  const { data, error } = await query;

  if (error) wrapSupabaseError(error);

  return new Map(
    (data || []).map((row) => [row.material_id, row.sell_price]),
  );
}

export async function buildSeedLinesForProject(
  projectId,
  { materialRows, timeLogs, actor = null },
) {
  const quoteLines = await loadProjectQuoteLines(projectId);
  const materialIds = [
    ...new Set((materialRows || []).map((row) => row.material_id).filter(Boolean)),
  ];
  const catalogByMaterialId = await loadCatalogSellByMaterialId(materialIds, actor);

  return buildSeedLinesFromOperationalData({
    materialRows,
    timeLogs,
    quoteLines,
    catalogByMaterialId,
  });
}

export async function updateInvoiceLineItem(actor, lineId, changes, userId) {
  requireInvoiceReview(actor);

  const payload = {
    ...changes,
    edited_at: new Date().toISOString(),
    edited_by: userId ?? null,
  };

  const { data, error } = await supabase
    .from("invoice_line_items")
    .update(payload)
    .eq("line_id", lineId)
    .select("*")
    .single();

  if (error) wrapSupabaseError(error);
  return data;
}

export async function insertInvoiceLineItem(actor, invoiceId, line, userId) {
  requireInvoiceReview(actor);

  const payload = {
    line_id: line.line_id || createInvoiceLineId(),
    invoice_id: invoiceId,
    sort_order: line.sort_order ?? 0,
    line_type: line.line_type,
    description: line.description,
    detail: line.detail ?? null,
    quantity: line.quantity ?? null,
    unit_label: line.unit_label ?? null,
    unit_amount: line.unit_amount ?? null,
    line_total: line.line_total,
    discount_percent: line.discount_percent ?? 0,
    line_status: line.line_status || LINE_STATUS.ACTIVE,
    pricing_source: line.pricing_source ?? null,
    reference_unit_cost: line.reference_unit_cost ?? null,
    reference_quoted_unit_price: line.reference_quoted_unit_price ?? null,
    reference_catalog_sell: line.reference_catalog_sell ?? null,
    source_quote_line_snapshot_id: line.source_quote_line_snapshot_id ?? null,
    source_time_log_id: line.source_time_log_id ?? null,
    source_usage_id: line.source_usage_id ?? null,
    metadata: line.metadata ?? null,
    edited_at: new Date().toISOString(),
    edited_by: userId ?? null,
  };

  const { data, error } = await supabase
    .from("invoice_line_items")
    .insert(payload)
    .select("*")
    .single();

  if (error) wrapSupabaseError(error);
  return data;
}

export async function deleteInvoiceLineItems(actor, lineIds = []) {
  requireInvoiceReview(actor);

  const ids = [...new Set((lineIds || []).filter(Boolean))];
  if (!ids.length) return;

  const { error } = await supabase
    .from("invoice_line_items")
    .delete()
    .in("line_id", ids);

  if (error) wrapSupabaseError(error);
}

export async function setInvoiceLineStatus(actor, lineId, lineStatus, userId) {
  return updateInvoiceLineItem(actor, lineId, { line_status: lineStatus }, userId);
}

export async function batchUpdateInvoiceLines(actor, lineIds, changes, userId) {
  requireInvoiceReview(actor);

  if (!lineIds?.length) return [];

  const now = new Date().toISOString();
  const results = [];

  for (const lineId of lineIds) {
    const { data, error } = await supabase
      .from("invoice_line_items")
      .update({
        ...changes,
        edited_at: now,
        edited_by: userId ?? null,
      })
      .eq("line_id", lineId)
      .select("*")
      .single();

    if (error) wrapSupabaseError(error);
    results.push(data);
  }

  return results;
}

export async function persistInvoiceReviewTotals(
  actor,
  invoiceId,
  {
    lineItems,
    projectDiscountPercent,
    goodwillAdjustment,
    reviewNotes,
    taxLines,
    userId,
    billingPeriodStart = null,
    billingPeriodEnd = null,
    seededAt = null,
  },
) {
  requireInvoiceReview(actor);

  const rollups = computeInvoiceReviewRollups({
    lineItems,
    projectDiscountPercent,
    goodwillAdjustment,
    taxLines,
  });
  const taxPayload = normalizeEstimateTaxLinesForSave(taxLines);

  const payload = {
    subtotal_labour: rollups.subtotalLabour,
    subtotal_materials: rollups.subtotalMaterials,
    subtotal: rollups.subtotal,
    total_tax: rollups.totalTax,
    total_amount: rollups.totalAmount,
    project_discount_percent: Number(projectDiscountPercent) || 0,
    goodwill_adjustment: Number(goodwillAdjustment) || 0,
    review_notes: reviewNotes?.trim() || null,
    billing_period_start: billingPeriodStart,
    billing_period_end: billingPeriodEnd,
    updated_by: userId ?? null,
    ...taxPayload,
  };

  if (seededAt) {
    payload.seeded_at = seededAt;
  }

  const { error } = await supabase
    .from("invoices")
    .update(payload)
    .eq("invoice_id", invoiceId)
    .eq("status", "draft");

  if (error) wrapSupabaseError(error);
  return rollups;
}

export async function recomputeAndSaveInvoiceFromLines(
  actor,
  invoiceId,
  lineItems,
  invoiceRecord,
  { taxLines, userId },
) {
  return persistInvoiceReviewTotals(actor, invoiceId, {
    lineItems,
    projectDiscountPercent: invoiceRecord.project_discount_percent,
    goodwillAdjustment: invoiceRecord.goodwill_adjustment,
    reviewNotes: invoiceRecord.review_notes,
    taxLines,
    userId,
    billingPeriodStart: invoiceRecord.billing_period_start,
    billingPeriodEnd: invoiceRecord.billing_period_end,
  });
}
